import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { usetifulService } from '../services/usetiful.service';
import { getAuthUser } from '../services/auth.service';
import { getActiveCompanyId } from '../services/company.service';
import { getCurrMember } from '../services/member.service';
import { getActiveAssessmentId } from '../services/assessment.service';
import { getActiveAgeGroupIds } from '../services/age-group.service';
import { GET_COMPANY_BY_ID } from '../queries/company.queries';

/**
 * Custom hook to set Usetiful tags with user and company data
 * Call this in components where you have access to user/company data
 * 
 * Handles the three-level role system:
 * 1. Super User roles - organization-wide permissions
 * 2. Assessment roles - assessment-specific permissions  
 * 3. Age Group roles - age group-specific permissions
 */
export const useUsetifulTags = () => {
  // Call hooks at the top level (not inside useEffect)
  const user = getAuthUser();
  const { member } = getCurrMember();
  const companyId = getActiveCompanyId();
  const assessmentId = getActiveAssessmentId();
  const ageGroupIds = getActiveAgeGroupIds();
  const ageGroupId = ageGroupIds?.[0]; // Use first active age group ID
  
  // Use proper hook for company data
  const { data: companyQueryData } = useQuery(GET_COMPANY_BY_ID, {
    skip: !companyId,
    variables: { _id: companyId },
    fetchPolicy: 'cache-and-network'
  });
  
  const company = companyQueryData?.company;

  useEffect(() => {
    let isMounted = true;
    
    // Only proceed if Usetiful is ready
    if (!usetifulService.isReady()) {
      return;
    }

    try {
      // Early return if component is unmounted
      if (!isMounted) return;
      // Set user tags if user exists
      if (user) {
        usetifulService.setUserTags(user);
      }

      // Set member roles (three-level hierarchy)
      if (member) {
        // Collect all roles at different levels
        const roleTags: Record<string, any> = {
          isAdmin: member.isAdmin || false,
          hasMemberData: true
        };

        // 1. Super User Roles (organization-wide)
        if (member.superUserRoles && member.superUserRoles.length > 0) {
          roleTags.superUserRoles = member.superUserRoles.join(',');
          roleTags.hasSuperUserRoles = true;
          
          // Set specific super user role flags
          member.superUserRoles.forEach((role: string) => {
            roleTags[`hasSuperUser_${role}`] = true;
          });
          
          // Specific tracking for Skills & Drills configuration role
          roleTags.hasConfigDrills = member.superUserRoles.includes('CONFIG_DRILLS');
          roleTags.hasSkillsDrillsConfig = member.superUserRoles.includes('CONFIG_DRILLS');
        }

        // 2. Assessment Roles
        const currentAssessmentRole = member.rolesByAssessment?.find((r: IMemberRoles) => r.assessmentId === assessmentId);
        if (currentAssessmentRole && currentAssessmentRole.roles?.length > 0) {
          roleTags.assessmentRoles = currentAssessmentRole.roles.join(',');
          roleTags.hasAssessmentRoles = true;
          roleTags.currentAssessmentId = assessmentId;
          
          // Set specific assessment role flags
          currentAssessmentRole.roles.forEach((role: string) => {
            roleTags[`hasAssessment_${role}`] = true;
          });
        }

        // 3. Age Group Roles
        const currentAgeGroupRole = member.rolesByAgeGroup?.find((r: IMemberRoles) => r.ageGroupId === ageGroupId);
        if (currentAgeGroupRole && currentAgeGroupRole.roles?.length > 0) {
          roleTags.ageGroupRoles = currentAgeGroupRole.roles.join(',');
          roleTags.hasAgeGroupRoles = true;
          roleTags.currentAgeGroupId = ageGroupId;
          
          // Set specific age group role flags  
          currentAgeGroupRole.roles.forEach((role: string) => {
            roleTags[`hasAgeGroup_${role}`] = true;
          });
        }

        // Determine highest role level
        if (member.isAdmin) {
          roleTags.highestRoleLevel = 'ADMIN';
        } else if (member.superUserRoles?.length > 0) {
          roleTags.highestRoleLevel = 'SUPER_USER';
        } else if (currentAssessmentRole && currentAssessmentRole.roles?.length > 0) {
          roleTags.highestRoleLevel = 'ASSESSMENT';
        } else if (currentAgeGroupRole && currentAgeGroupRole.roles?.length > 0) {
          roleTags.highestRoleLevel = 'AGE_GROUP';
        } else {
          roleTags.highestRoleLevel = 'NONE';
        }

        usetifulService.setTags(roleTags);
      }

      // Set company tags if company exists
      if (company) {
        usetifulService.setCompanyTags(company);
      }

      // Set context tags
      usetifulService.setTags({
        currentAssessmentId: assessmentId || 'none',
        currentAgeGroupId: ageGroupId || 'none',
        hasActiveAssessment: !!assessmentId,
        hasActiveAgeGroup: !!ageGroupId
      });

    } catch (error) {
      console.error('❌ Failed to set Usetiful user/company tags:', error);
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [user, member, companyId, assessmentId, ageGroupId, company]); // Re-run when context changes
};

/**
 * Hook to manually update Usetiful tags with custom data
 */
export const useSetUsetifulTags = () => {
  return (tags: Record<string, string | number | boolean>) => {
    if (usetifulService.isReady()) {
      usetifulService.setTags(tags);
    }
  };
};